import React from "react";
import { chartReady } from "../../lib/numberSenseData";
import { Icon, Keys } from "../numberSenseUi";

/* ============================================================
   Number Sense — the "practice & play" hub. Everything that isn't
   the lesson path lives here so the home screen can stay focused on
   the course: Practice (drills), Play (the band room), and More
   (reference + utilities).

   Props: gameData + the same handlers the home screen used to host,
   plus onBack.
   ============================================================ */
const Section = ({ children }) => <div className="home-section">{children}</div>;

const Entry = ({ icon, title, sub, onClick, soon }) => (
  <button
    className={`games-entry ${soon ? "soon" : ""}`}
    onClick={soon ? undefined : onClick}
    disabled={soon}
  >
    <div className="ic">
      <Icon name={soon ? "lock" : icon} />
    </div>
    <div className="txt">
      <div className="ttl">
        {title}
        {soon && <span className="soon-chip">soon</span>}
      </div>
      <div className="sub">{sub}</div>
    </div>
    {!soon && <span className="chev">›</span>}
  </button>
);

const MenuScreen = ({
  gameData,
  onOpenGames,
  onReview,
  onOpenDrill,
  onOpenFlow,
  onOpenJam,
  onOpenTrainer,
  onOpenChart,
  onOpenLearn,
  onOpenStats,
  onOpenSync,
  onOpenSettings,
  onBack,
}) => {
  const canReview = gameData.pairs.length >= 3;
  // chart mode wants a key change to be possible — two chart-ready keys
  const canChart = chartReady(gameData.unlocked);
  return (
    <div className="ns-root">
      <div className="ns-app">
        <div className="subbar">
          <button className="iconbtn" onClick={onBack} aria-label="back">
            <Icon name="back" />
          </button>
          <div className="subbar-title">practice &amp; play</div>
        </div>
        <div className="scroll">
          <Section>practice</Section>
          <Entry
            icon="cards"
            title="practice arcade"
            sub={
              gameData.unlocked.length
                ? `drill your ${gameData.unlocked.length} unlocked chords`
                : "finish a lesson to unlock games"
            }
            onClick={onOpenGames}
          />
          {canReview && (
            <Entry
              icon="swap"
              title="review your weak spots"
              sub="a quick mixed lesson, weighted to the chords you miss most"
              onClick={onReview}
            />
          )}
          <Entry
            icon="bolt"
            title="targeted drills"
            sub="grind one key's scale & chords, or your weak spots"
            onClick={onOpenDrill}
          />
          <Entry
            icon="piano"
            title="keyboard flow"
            sub="MIDI rapid-fire — it calls a chord, you play it. no buttons"
            onClick={onOpenFlow}
          />

          <Section>play</Section>
          <Entry
            icon="music"
            title="jam room"
            sub="comp over a looping groove — drums, bass and your changes"
            onClick={onOpenJam}
          />
          <Entry
            icon="timer"
            title="pattern trainer"
            sub="learn a comping feel — hear it, play it back, get a match score"
            onClick={onOpenTrainer}
          />
          <Entry
            icon="note"
            title="chart mode"
            sub={
              canChart
                ? "cold-read a numbers-only chart with the band — key changes included"
                : "unlocks once two keys can play a loop — the band calls the key"
            }
            onClick={onOpenChart}
            soon={!canChart}
          />

          <Section>more</Section>
          <Entry
            icon="book"
            title="learn"
            sub="the ideas behind the drills — foundations and every concept"
            onClick={onOpenLearn}
          />
          <Entry
            icon="chart"
            title="your stats"
            sub="XP trend, accuracy by key, and which chords are sticking"
            onClick={onOpenStats}
          />
          <Entry
            icon="link"
            title="carry your progress"
            sub="move your XP and streak to another device with a recovery code"
            onClick={onOpenSync}
          />
          <Entry
            icon="gear"
            title="settings"
            sub="mute, ear-training tempo, and count-in"
            onClick={onOpenSettings}
          />

          <div className="footnote">
            <Keys />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuScreen;
